'use client';

import React from 'react';
import styles from './OntologyPreJoin.module.css';

type OntologyFolder = {
  path: string;
  title?: string;
  group?: string;
};

type ChildFolder = {
  path: string;
  name: string;
  selectable?: boolean;
  hasChildren?: boolean;
};

type TreeNode = {
  path: string;
  label: string;
  children: TreeNode[];
  folder: OntologyFolder | null;
  hasChildren: boolean;
  childrenLoaded: boolean;
  loading: boolean;
  searchVisible?: boolean;
  searchExpanded?: boolean;
};

const DEMO_ROOT: ChildFolder[] = [
  { path: 'Company/🟧 AI R&D', name: '🟧 AI R&D', selectable: true, hasChildren: false },
  { path: 'Company/🟧 Продажи/Лиды', name: 'Лиды', selectable: false, hasChildren: true },
];

const DEMO_LEADS: ChildFolder[] = [
  { path: 'Company/🟧 Продажи/Лиды/agima-sql', name: 'agima-sql', selectable: false, hasChildren: true },
  {
    path: 'Company/🟧 Продажи/Лиды/Неклассифицированные',
    name: 'Неклассифицированные',
    selectable: false,
    hasChildren: true,
  },
];

function displayLabel(label: string): string {
  const stripped = label.replace(/^(?:\p{Extended_Pictographic}\uFE0F?\s*)+/u, '').trim();
  return stripped || label;
}

function compareNodes(a: TreeNode, b: TreeNode): number {
  return a.label.localeCompare(b.label, 'ru');
}

function nodesFromItems(items: ChildFolder[]): TreeNode[] {
  return items
    .map((item) => ({
      path: item.path,
      label: item.name,
      children: [] as TreeNode[],
      folder: item.selectable ? { path: item.path, title: item.name } : null,
      hasChildren: !!item.hasChildren,
      childrenLoaded: !item.hasChildren,
      loading: false,
    }))
    .sort(compareNodes);
}

function patchNode(nodes: TreeNode[], path: string, patch: Partial<TreeNode>): TreeNode[] {
  return nodes.map((node) => {
    if (node.path === path) return { ...node, ...patch };
    if (path.startsWith(`${node.path}/`)) {
      return { ...node, children: patchNode(node.children, path, patch) };
    }
    return node;
  });
}

function findFolder(nodes: TreeNode[], path: string): OntologyFolder | null {
  for (const node of nodes) {
    if (node.folder && node.folder.path === path) return node.folder;
    const nested = findFolder(node.children, path);
    if (nested) return nested;
  }
  return null;
}

function folderFromPath(path: string): OntologyFolder {
  const parts = path.split('/').filter(Boolean);
  return { path, title: parts[parts.length - 1] || path };
}

function fetchFolderChildren(path?: string): Promise<{ items: ChildFolder[]; error?: string }> {
  const url = path
    ? '/api/ontology/folders/children?path=' + encodeURIComponent(path)
    : '/api/ontology/folders/children';
  return fetch(url, { credentials: 'same-origin' }).then((response) => {
    if (!response.ok) {
      return response
        .json()
        .catch(() => ({}))
        .then((body: { error?: string }) => ({
          items: [] as ChildFolder[],
          error: body.error || 'HTTP ' + response.status,
        }));
    }
    return response.json().then((body: { items?: ChildFolder[] }) => ({
      items: body.items || [],
    }));
  });
}

function nodeSearchText(node: TreeNode): string {
  return `${node.label} ${node.path}`.toLocaleLowerCase('ru');
}

function markSearchMatches(nodes: TreeNode[], query: string) {
  function visit(node: TreeNode): boolean {
    let hasMatchingChild = false;
    node.children.forEach((child) => {
      if (visit(child)) hasMatchingChild = true;
    });
    const selfMatches = !query || nodeSearchText(node).indexOf(query) !== -1;
    node.searchVisible = selfMatches || hasMatchingChild;
    node.searchExpanded = !!query && hasMatchingChild;
    return !!node.searchVisible;
  }
  nodes.forEach(visit);
}

function setPromptedCookie(room: string) {
  document.cookie =
    'octor_ontology_prompted=' + encodeURIComponent(room) + '; Path=/; Max-Age=14400; Secure; SameSite=Lax';
}

function SearchIcon() {
  return (
    <svg className={styles.searchIcon} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="7" cy="7" r="4.25" stroke="currentColor" strokeWidth="1.5" />
      <path d="M10.2 10.2 13.5 13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function FolderIcon({ kind }: { kind: 'leaf' | 'branch' | 'open' }) {
  if (kind === 'open') {
    return (
      <svg className={styles.folderIcon} viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path
          d="M2.2 6.4V4.7A1.4 1.4 0 0 1 3.6 3.3h2.2c.28 0 .54.12.72.34l.55.7h5.33A1.4 1.4 0 0 1 13.8 5.7v.7"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinejoin="round"
        />
        <path
          d="M2.15 6.6h11.7c.7 0 1.2.66 1.05 1.34l-.9 4.05A1.4 1.4 0 0 1 12.64 13H3.36a1.4 1.4 0 0 1-1.36-1.01L1.1 7.94C.95 7.26 1.45 6.6 2.15 6.6Z"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (kind === 'branch') {
    return (
      <svg className={styles.folderIcon} viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path
          d="M4.1 3.15h2.15c.28 0 .54.13.71.35l.42.55h4.05A1.25 1.25 0 0 1 12.7 5.3v.55"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinejoin="round"
          opacity="0.55"
        />
        <path
          d="M2.2 5.7A1.45 1.45 0 0 1 3.65 4.25h2.25c.3 0 .58.14.76.38l.58.77h4.55A1.45 1.45 0 0 1 13.25 6.85v5.2A1.45 1.45 0 0 1 11.8 13.5H3.65A1.45 1.45 0 0 1 2.2 12.05v-6.35Z"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return (
    <svg className={styles.folderIcon} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M2.25 4.75A1.5 1.5 0 0 1 3.75 3.25h2.4c.3 0 .58.14.76.38l.7.92h4.64a1.5 1.5 0 0 1 1.5 1.5v5.7a1.5 1.5 0 0 1-1.5 1.5H3.75a1.5 1.5 0 0 1-1.5-1.5v-7Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className={styles.check} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M3.5 8.3 6.4 11.2 12.5 4.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function OntologyPreJoin(props: { room: string; onDone: () => void; demo?: boolean }) {
  const { room, onDone, demo } = props;
  const [status, setStatus] = React.useState('Проверяем папку ontology…');
  const [statusKind, setStatusKind] = React.useState<'info' | 'warning' | 'error'>('info');
  const [pickerVisible, setPickerVisible] = React.useState(false);
  const [rootNodes, setRootNodes] = React.useState<TreeNode[]>([]);
  const [catalogError, setCatalogError] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [selectedPath, setSelectedPath] = React.useState('');
  const [expandedPaths, setExpandedPaths] = React.useState<Record<string, boolean>>({});
  const [busy, setBusy] = React.useState(false);
  const doneRef = React.useRef(false);

  const enterMeeting = React.useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    setPromptedCookie(room);
    onDone();
  }, [onDone, room]);

  const revealPicker = React.useCallback(() => {
    setPickerVisible(true);
    setStatus('Загружаем папки…');
  }, []);

  React.useEffect(() => {
    if (!demo) return;
    setPickerVisible(true);
    setRootNodes(nodesFromItems(DEMO_ROOT));
    setStatus('Выберите папку для встречи');
  }, [demo]);

  React.useEffect(() => {
    if (demo) return;
    let cancelled = false;

    function load() {
      fetch('/api/ontology/prejoin/' + encodeURIComponent(room), { credentials: 'same-origin' })
        .then((response) => {
          if (response.status === 401) throw new Error('Нужно войти в аккаунт, чтобы выбрать папку.');
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.json();
        })
        .then((state) => {
          if (cancelled) return null;
          if (state && state.ontologyFolder) {
            setStatus('Папка уже выбрана. Подключаем…');
            enterMeeting();
            return null;
          }
          if (!state || !state.showFolderPicker) {
            enterMeeting();
            return null;
          }
          revealPicker();
          return fetchFolderChildren().then((result) => {
            if (cancelled) return;
            setRootNodes(nodesFromItems(result.items));
            setCatalogError(result.error || '');
            if (!result.error) setStatus('Выберите папку для встречи');
          });
        })
        .catch(function (error) {
          if (!cancelled) enterMeeting();
          return error;
        });
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [demo, enterMeeting, revealPicker, room]);

  const query = search.trim().toLocaleLowerCase('ru');
  markSearchMatches(rootNodes, query);

  const selectedFolder = selectedPath
    ? findFolder(rootNodes, selectedPath) || folderFromPath(selectedPath)
    : null;
  const visibleCount = query
    ? rootNodes.reduce((count, node) => count + (node.searchVisible ? 1 : 0), 0)
    : rootNodes.length;

  const loadChildren = React.useCallback(async (node: TreeNode) => {
    if (node.childrenLoaded || node.loading || !node.hasChildren) return;
    setRootNodes((current) => patchNode(current, node.path, { loading: true }));
    try {
      const result = demo
        ? { items: node.path.endsWith('/Лиды') ? DEMO_LEADS : [], error: '' }
        : await fetchFolderChildren(node.path);
      setRootNodes((current) =>
        patchNode(current, node.path, {
          loading: false,
          childrenLoaded: true,
          children: nodesFromItems(result.items),
          hasChildren: result.items.length > 0,
        }),
      );
      if (result.error) setCatalogError(result.error);
    } catch (error) {
      setRootNodes((current) => patchNode(current, node.path, { loading: false }));
      setCatalogError(error instanceof Error ? error.message : 'Не удалось загрузить папки');
    }
  }, [demo]);

  async function confirmFolder() {
    if (!selectedFolder) return;
    setBusy(true);
    if (demo) {
      enterMeeting();
      return;
    }
    try {
      const response = await fetch('/api/ontology/prejoin/' + encodeURIComponent(room), {
        method: 'PUT',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ontologyFolder: selectedFolder.path }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'HTTP ' + response.status);
      enterMeeting();
    } catch (error) {
      setStatusKind('error');
      setStatus(error instanceof Error ? error.message : 'Не удалось сохранить папку');
      setBusy(false);
    }
  }

  function toggleExpanded(node: TreeNode) {
    const next = !expandedPaths[node.path];
    setExpandedPaths((current) => ({ ...current, [node.path]: next }));
    if (next) void loadChildren(node);
  }

  function renderNode(node: TreeNode, level: number, isLast: boolean): React.ReactNode {
    if (query && !node.searchVisible) return null;
    const hasChildren = node.hasChildren || node.children.length > 0 || node.loading;
    const expanded = query ? !!node.searchExpanded : !!expandedPaths[node.path];
    const selected = !!node.folder && node.folder.path === selectedPath;
    const selectable = !!node.folder;
    const folderKind = hasChildren ? (expanded ? 'open' : 'branch') : 'leaf';
    const visibleChildren =
      hasChildren && expanded ? node.children.filter((child) => !query || child.searchVisible) : [];
    return (
      <React.Fragment key={node.path}>
        <div
          className={`${styles.row} ${level > 0 ? styles.rowChild : ''} ${isLast ? styles.rowLast : ''}`}
          style={{ ['--tree-level' as string]: String(level) }}
        >
          <button
            type="button"
            className={`${styles.item} ${!selectable ? styles.itemGroup : ''} ${level > 0 ? styles.itemChild : ''}`}
            role="treeitem"
            aria-level={level + 1}
            aria-selected={selected}
            aria-expanded={hasChildren ? expanded : undefined}
            title={hasChildren && !selectable ? (expanded ? 'Свернуть' : 'Показать вложенные папки') : undefined}
            onClick={() => {
              if (selectable) setSelectedPath(node.folder!.path);
              else if (hasChildren && !query) toggleExpanded(node);
            }}
          >
            <FolderIcon kind={folderKind} />
            <span className={styles.name}>{displayLabel(node.label)}</span>
            {node.loading ? <span className={styles.loading}>…</span> : null}
            {selected ? <CheckIcon /> : null}
          </button>
        </div>
        {visibleChildren.map((child, index) => renderNode(child, level + 1, index === visibleChildren.length - 1))}
      </React.Fragment>
    );
  }

  if (!pickerVisible) {
    return (
      <div className={styles.root}>
        <p className={styles.status}>{status}</p>
      </div>
    );
  }

  const banner = catalogError || (statusKind !== 'info' ? status : '');

  return (
    <div className={styles.root}>
      {banner ? <div className={`${styles.status} ${styles[statusKind]}`}>{banner}</div> : null}
      <div className={styles.search}>
        <label className={styles.label} htmlFor="ontology-search">
          Поиск папки
        </label>
        <div className={styles.searchField}>
          <SearchIcon />
          <input
            id="ontology-search"
            className={styles.input}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Поиск в открытых папках"
          />
        </div>
      </div>
      <div id="folder-tree" role="tree" className={styles.tree}>
        {visibleCount === 0 ? (
          <div className={styles.empty}>{query ? 'Ничего не найдено в открытых папках' : 'Папки ещё не загрузились'}</div>
        ) : (
          rootNodes.map((node, index) => renderNode(node, 0, index === rootNodes.length - 1))
        )}
      </div>
      <div className={styles.meta}>
        {selectedFolder ? (
          <span className={styles.chip}>
            <CheckIcon />
            <span className={styles.chipName}>{displayLabel(selectedFolder.title || selectedFolder.path)}</span>
          </span>
        ) : (
          'Папка ещё не выбрана.'
        )}
      </div>
      <div className={styles.actions}>
        <button
          id="confirm"
          type="button"
          className={styles.primary}
          disabled={busy || !selectedFolder}
          onClick={confirmFolder}
        >
          Продолжить с папкой
        </button>
        <button id="skip" type="button" className={styles.secondary} disabled={busy} onClick={enterMeeting}>
          Войти без папки
        </button>
      </div>
    </div>
  );
}
